import { useEffect, useState } from 'react'
import { fbt } from 'fbtee'
import { toast } from 'sonner'
import { FlaskConical, Loader2, RefreshCw } from 'lucide-react'
import { BeaconLogo } from './BeaconLogo'
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover'
import { Button } from './ui/button'
import { useAuth } from '../hooks/useAuth'
import { exitPreview, getRunningBundleVersion, refreshPreviewChannel } from '../lib/ota'
import { classifyError, formatErrorMessage } from '../lib/errors'
import { log } from '../lib/logger'

interface PreviewBuildMenuProps {
  channel: string
  logo: React.ComponentProps<typeof BeaconLogo>
}

type Busy = 'refresh' | 'exit' | null

// The header logo while a preview bundle is loaded. A preview reads and writes
// real data, so the badge has to be unmissable; the popover is where an admin
// pulls a republished bundle or goes back to production.
export function PreviewBuildMenu({ channel, logo }: PreviewBuildMenuProps) {
  const { isAdmin } = useAuth()
  const [open, setOpen] = useState(false)
  const [version, setVersion] = useState<string | null>(null)
  const [busy, setBusy] = useState<Busy>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open || version) return
    getRunningBundleVersion()
      .then(setVersion)
      .catch(err => log('ota', 'read running bundle failed', { error: String(err) }))
  }, [open, version])

  // Both actions restart the app on success, so only the failure and
  // up-to-date paths ever return here.
  async function run(action: Exclude<Busy, null>) {
    setBusy(action)
    setError(null)
    try {
      if (action === 'exit') {
        await exitPreview()
        return
      }
      const result = await refreshPreviewChannel()
      if (result.updated) return
      toast.success(fbt('Preview is up to date', 'Toast when refreshing a preview build finds no newer bundle'), {
        description: result.version,
      })
      setOpen(false)
    } catch (err) {
      log('ota', `${action} preview failed`, { channel, error: String(err) })
      setError(formatErrorMessage(classifyError(err)))
    }
    setBusy(null)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="relative flex cursor-pointer rounded-full ring-2 ring-amber-400"
          aria-label={fbt('Preview build loaded — switch back to the released app', 'Accessible label for the header logo while a preview build is loaded')}
        >
          <BeaconLogo {...logo} className="h-11 w-11" />
          <span
            aria-hidden="true"
            className="absolute -bottom-0.5 -right-0.5 flex size-4 items-center justify-center rounded-full bg-amber-400 text-background"
          >
            <FlaskConical className="size-2.5" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 space-y-3">
        <div className="space-y-0.5">
          <div className="flex items-center gap-2 text-sm">
            <FlaskConical className="size-4 shrink-0 text-amber-400" />
            <span>
              <fbt desc="Label shown when an unreleased preview build is loaded in the app">
                Preview build
              </fbt>{' '}
              <span className="font-mono font-medium">{channel}</span>
            </span>
          </div>
          {version && <p className="pl-6 font-mono text-xs text-muted-foreground">{version}</p>}
        </div>
        <p className="text-xs text-muted-foreground">
          <fbt desc="Explanation shown in the preview build popover">
            This is an unreleased build using real data.
          </fbt>
        </p>
        {isAdmin && (
          <Button size="sm" variant="outline" className="w-full" onClick={() => void run('refresh')} disabled={busy !== null}>
            {busy === 'refresh' ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
            <fbt desc="Button label to check for and load a newer bundle of the current preview build">
              Refresh preview
            </fbt>
          </Button>
        )}
        <Button size="sm" className="w-full" onClick={() => void run('exit')} disabled={busy !== null}>
          {busy === 'exit' ? (
            <>
              <Loader2 className="size-4 animate-spin" />
              <fbt desc="Button label while the app switches back to the released build">Switching…</fbt>
            </>
          ) : (
            <fbt desc="Button label to leave a preview build and return to the released app">
              Back to the real app
            </fbt>
          )}
        </Button>
        {error && <p className="text-xs text-destructive">{error}</p>}
      </PopoverContent>
    </Popover>
  )
}
